import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  DollarSign,
  CheckCircle,
  Send,
  X,
  Filter,
  Search,
  Calendar,
  Building
} from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import { sendJobApplication, sendEmailFallback, JobApplicationData } from '../utils/emailService';

const Jobs: React.FC = () => {
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [applicationData, setApplicationData] = useState<JobApplicationData>({
    jobTitle: '',
    applicantName: '',
    applicantEmail: '',
    applicantPhone: '',
    resume: '',
    positionType: 'Full-time',
    coverLetter: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Job data from OntimeStaff website
  const jobs = [
    {
      id: 1,
      title: 'General Labour',
      category: 'General Labour',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: 'Competitive',
      description: 'We are looking for reliable and hardworking general labour workers to join our team. This position involves various manual tasks and physical work.',
      requirements: [
        'Physical ability to perform manual tasks',
        'Reliable and punctual',
        'Ability to work in various weather conditions',
        'Basic English communication skills',
        'Valid work permit or Canadian citizenship'
      ],
      benefits: [
        'Competitive hourly rates',
        'Overtime opportunities',
        'Safety training provided',
        'Potential for permanent placement'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-15',
      urgent: true
    },
    {
      id: 2,
      title: 'Assembly Worker',
      category: 'Assembly',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: '$18-22/hour',
      description: 'Join our assembly team and help manufacture quality products. This role involves assembling components according to specifications.',
      requirements: [
        'Previous assembly experience preferred',
        'Attention to detail',
        'Ability to work in a fast-paced environment',
        'Good hand-eye coordination',
        'Ability to read and follow instructions'
      ],
      benefits: [
        'Competitive hourly rates',
        'Health benefits after probation',
        'Paid training',
        'Career advancement opportunities'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-14',
      urgent: false
    },
    {
      id: 3,
      title: 'Packaging Worker',
      category: 'Packaging',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: '$17-20/hour',
      description: 'We need packaging workers to handle product packaging and shipping operations. This role ensures products are properly packaged and ready for distribution.',
      requirements: [
        'Physical ability to lift up to 50 lbs',
        'Experience with packaging equipment preferred',
        'Attention to detail',
        'Ability to work in a team environment',
        'Reliable and punctual'
      ],
      benefits: [
        'Competitive hourly rates',
        'Overtime opportunities',
        'Safety equipment provided',
        'Potential for permanent placement'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-13',
      urgent: true
    },
    {
      id: 4,
      title: 'Manufacturing Worker',
      category: 'Manufacturing',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: '$19-24/hour',
      description: 'Join our manufacturing team and contribute to the production of quality goods. This role involves operating machinery and following safety protocols.',
      requirements: [
        'Previous manufacturing experience preferred',
        'Ability to operate machinery',
        'Strong attention to detail',
        'Ability to work in a team environment',
        'Safety-conscious mindset'
      ],
      benefits: [
        'Competitive hourly rates',
        'Health and dental benefits',
        'Paid training and certification',
        'Career advancement opportunities'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-12',
      urgent: false
    },
    {
      id: 5,
      title: 'Forklift Operator',
      category: 'Fork-lifting',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: '$20-25/hour',
      description: 'We are seeking certified forklift operators to handle material movement and warehouse operations. This role requires a valid forklift license.',
      requirements: [
        'Valid forklift operator license',
        'Previous forklift experience',
        'Ability to work in a warehouse environment',
        'Good communication skills',
        'Safety-conscious approach'
      ],
      benefits: [
        'Competitive hourly rates',
        'Health benefits',
        'Safety training provided',
        'Potential for permanent placement'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-11',
      urgent: true
    },
    {
      id: 6,
      title: 'Storage Worker',
      category: 'Storage',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: '$16-19/hour',
      description: 'Join our storage team and help manage inventory and warehouse operations. This role involves organizing and maintaining storage areas.',
      requirements: [
        'Physical ability to lift and move items',
        'Organizational skills',
        'Ability to work independently',
        'Basic computer skills preferred',
        'Reliable and punctual'
      ],
      benefits: [
        'Competitive hourly rates',
        'Overtime opportunities',
        'Safety training provided',
        'Potential for permanent placement'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-10',
      urgent: false
    },
    {
      id: 7,
      title: 'Moving Assistant',
      category: 'Moving',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: '$18-22/hour',
      description: 'We need moving assistants to help with residential and commercial moves. This role involves physical work and customer interaction.',
      requirements: [
        'Physical ability to lift heavy items',
        'Valid driver\'s license preferred',
        'Customer service skills',
        'Ability to work flexible hours',
        'Reliable and trustworthy'
      ],
      benefits: [
        'Competitive hourly rates',
        'Tips from customers',
        'Flexible scheduling',
        'Potential for permanent placement'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-09',
      urgent: true
    },
    {
      id: 8,
      title: 'IT Support Specialist',
      category: 'IT',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: '$25-35/hour',
      description: 'Join our IT team and provide technical support to our clients. This role involves troubleshooting and maintaining computer systems.',
      requirements: [
        'Degree or diploma in IT or related field',
        'Previous IT support experience',
        'Knowledge of Windows and Mac systems',
        'Strong problem-solving skills',
        'Excellent communication skills'
      ],
      benefits: [
        'Competitive hourly rates',
        'Health and dental benefits',
        'Professional development opportunities',
        'Career advancement potential'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-08',
      urgent: false
    },
    {
      id: 9,
      title: 'Administrative Assistant',
      category: 'Administrative',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: '$18-23/hour',
      description: 'We are looking for an administrative assistant to support our office operations. This role involves various clerical and administrative tasks.',
      requirements: [
        'Previous administrative experience',
        'Proficiency in Microsoft Office',
        'Excellent communication skills',
        'Organizational abilities',
        'Attention to detail'
      ],
      benefits: [
        'Competitive hourly rates',
        'Health benefits',
        'Paid vacation',
        'Career advancement opportunities'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-07',
      urgent: false
    },
    {
      id: 10,
      title: 'Clerical Worker',
      category: 'Clerical',
      location: 'Ontario, Canada',
      type: 'Full-time',
      salary: '$16-20/hour',
      description: 'Join our clerical team and help with various office tasks including data entry, filing, and customer service.',
      requirements: [
        'Previous clerical experience preferred',
        'Basic computer skills',
        'Good communication skills',
        'Ability to work in a team',
        'Reliable and punctual'
      ],
      benefits: [
        'Competitive hourly rates',
        'Overtime opportunities',
        'Paid training',
        'Potential for permanent placement'
      ],
      company: 'OntimeStaff',
      postedDate: '2024-01-06',
      urgent: false
    }
  ];

  const categories = ['All', 'General Labour', 'Assembly', 'Packaging', 'Manufacturing', 'Fork-lifting', 'Storage', 'Moving', 'IT', 'Administrative', 'Clerical'];

  const handleApplyClick = (job: any) => {
    setSelectedJob(job);
    setApplicationData({
      jobTitle: job.title,
      applicantName: '',
      applicantEmail: '',
      applicantPhone: '',
      resume: '',
      positionType: 'Full-time',
      coverLetter: `I am interested in applying for the ${job.title} position.`
    });
    setShowApplicationModal(true);
    setIsSubmitted(false);
  };

  const handleApplicationInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setApplicationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplicationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const success = await sendJobApplication(applicationData);
      
      if (success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setShowApplicationModal(false);
          setIsSubmitted(false);
        }, 3000);
      } else {
        sendEmailFallback(applicationData, 'job');
        setIsSubmitted(true);
        setTimeout(() => {
          setShowApplicationModal(false);
          setIsSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      sendEmailFallback(applicationData, 'job');
      setIsSubmitted(true);
      setTimeout(() => {
        setShowApplicationModal(false);
        setIsSubmitted(false);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.requirements.some(req => req.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || job.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Find Your <span className="text-gradient">Dream Job</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Discover exciting career opportunities across various industries. 
              We connect talented individuals with leading companies in the Greater Toronto Area.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="section-padding bg-white border-b border-gray-200">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search jobs by title, description, or skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Category Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="text-center">
              <p className="text-gray-600">
                Showing {filteredJobs.length} of {jobs.length} available positions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Jobs Grid */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="h-full"
              >
                <Card className="p-6 h-full flex flex-col hover:shadow-lg transition-shadow duration-300">
                  {/* Job Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-display font-bold text-gray-900">
                          {job.title}
                        </h3>
                        {job.urgent && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                            Urgent
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-gray-600 mb-2">
                        <Building className="w-4 h-4 mr-2" />
                        <span className="text-sm">{job.company}</span>
                      </div>
                      <div className="flex items-center text-gray-600 mb-2">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span className="text-sm">{job.location}</span>
                      </div>
                    </div>
                  </div>

                  {/* Job Details */}
                  <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{job.type}</span>
                    </div>
                    <div className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1" />
                      <span>{job.salary}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>{new Date(job.postedDate).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Job Description */}
                  <p className="text-gray-600 mb-4 flex-1">
                    {job.description}
                  </p>

                  {/* Key Requirements */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Requirements:</h4>
                    <ul className="space-y-1">
                      {job.requirements.slice(0, 3).map((requirement, reqIndex) => (
                        <li key={reqIndex} className="flex items-start text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{requirement}</span>
                        </li>
                      ))}
                      {job.requirements.length > 3 && (
                        <li className="text-sm text-gray-500">
                          +{job.requirements.length - 3} more requirements
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Apply Button */}
                  <Button
                    onClick={() => handleApplyClick(job)}
                    className="w-full bg-gradient-to-r from-primary-600 to-accent-600 hover:from-primary-700 hover:to-accent-700 text-white flex items-center justify-center space-x-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Apply Now</span>
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No jobs found</h3>
              <p className="text-gray-600">Try adjusting your search criteria or check back later for new opportunities.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom">
          <motion.div
            className="text-center text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-6">
              Don't See What You're Looking For?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Submit your resume and we'll match you with roles you qualify for. 
              Our team will contact you when suitable opportunities arise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-100">
                Submit Resume
              </Button>
              <Link to="/contact">
                <Button size="lg" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 focus:ring-white">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Modal */}
      {showApplicationModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-display font-bold text-gray-900">
                  Apply for {selectedJob.title}
                </h2>
                <p className="text-gray-600">{selectedJob.company} • {selectedJob.location}</p>
              </div>
              <button
                onClick={() => setShowApplicationModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              {isSubmitted ? (
                <motion.div
                  className="text-center py-8"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Application Sent!</h3>
                  <p className="text-gray-600">
                    Thank you for your interest in the {selectedJob.title} position. We'll contact you within 24 hours.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleApplicationSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="applicantName" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="applicantName"
                        name="applicantName"
                        required
                        value={applicationData.applicantName}
                        onChange={handleApplicationInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="applicantEmail" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="applicantEmail"
                        name="applicantEmail"
                        required
                        value={applicationData.applicantEmail}
                        onChange={handleApplicationInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="applicantPhone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="applicantPhone"
                        name="applicantPhone"
                        required
                        value={applicationData.applicantPhone}
                        onChange={handleApplicationInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="positionType" className="block text-sm font-medium text-gray-700 mb-2">
                        Position Type *
                      </label>
                      <select
                        id="positionType"
                        name="positionType"
                        required
                        value={applicationData.positionType}
                        onChange={handleApplicationInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="Full-time">Full-time</option>
                        <option value="Part-time">Part-time</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-2">
                      Resume / CV *
                    </label>
                    <textarea
                      id="resume"
                      name="resume"
                      required
                      rows={4}
                      value={applicationData.resume}
                      onChange={handleApplicationInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      placeholder="Please paste your resume/CV here or provide a brief summary of your work experience, skills, and qualifications..."
                    />
                  </div>

                  <div>
                    <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-700 mb-2">
                      Cover Letter / Additional Information
                    </label>
                    <textarea
                      id="coverLetter"
                      name="coverLetter"
                      rows={4}
                      value={applicationData.coverLetter}
                      onChange={handleApplicationInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      placeholder="Tell us why you're interested in this position and highlight your relevant experience..."
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-primary-600 to-accent-600 hover:from-primary-700 hover:to-accent-700 text-white flex items-center justify-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>Submit Application</span>
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowApplicationModal(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
